import React, { useMemo } from 'react'
import { Avatar, ChipProps } from '@node-space/ui-components'
import { useActiveWallets } from 'hooks/useActiveWallets'
import { BeneficiaryType } from 'types/beneficiaries'
import { WalletType } from 'types/types'
import { floorWithPrecision } from 'utils/utils'
import { SourcePayWallet } from '../../Pay/types'
import { PayoutBeneficiaryFilters } from '../constants'

interface BeneficiaryFormContextProps {
  wallets: WalletType[]
  wallet: SourcePayWallet
  filters: ChipProps[]
}

const useBeneficiaryFormContext = ({ filters, wallet, wallets }: BeneficiaryFormContextProps) => {
  const activeWallets = useActiveWallets(wallets)

  const sourceWalletOptions = useMemo(
    () =>
      activeWallets?.map(wallet => ({
        LeftElement: <Avatar name={wallet?.currency?.code?.toLowerCase()} size="small" />,
        value: wallet?.lsid,
        leftText: wallet?.description,
        rightText: `${floorWithPrecision(wallet?.balance || 0, wallet?.currency?.quantityPrecision)} ${wallet?.currency?.code}`,
      })),
    [activeWallets]
  )

  const beneficiaryType: BeneficiaryType = useMemo(() => {
    const isFirstPartyPayoutsEnabled = filters?.find(
      filter => filter?.name === PayoutBeneficiaryFilters.MY_COMPANY
    )
    const isThirdPartyPayoutsEnabled = filters?.find(
      filter => filter?.name === PayoutBeneficiaryFilters.BUSINESS
    )

    if (isFirstPartyPayoutsEnabled && isThirdPartyPayoutsEnabled) {
      return
    }

    if (isThirdPartyPayoutsEnabled) {
      return BeneficiaryType.THIRD_PARTY
    }

    if (isFirstPartyPayoutsEnabled) {
      return BeneficiaryType.FIRST_PARTY
    }
  }, [filters])

  const isInsufficientBalance = useMemo(() => {
    if (!wallet) return false
    return wallet?.balance <= 0
  }, [wallet])

  const isPayoutsSupported = useMemo(() => {
    if (!wallet) return false
    return wallet && !!filters?.length
  }, [wallet, filters])

  const showCallout = useMemo(() => {
    if (!wallet) return false

    if (isInsufficientBalance && isPayoutsSupported) {
      return true
    }

    if (!isPayoutsSupported && !isInsufficientBalance) {
      return true
    }
  }, [wallet, isInsufficientBalance, isPayoutsSupported])

  return {
    activeWallets,
    beneficiaryType,
    isInsufficientBalance,
    isPayoutsSupported,
    showCallout,
    sourceWalletOptions,
  }
}

export default useBeneficiaryFormContext
